import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Alert, CircularProgress, IconButton } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';

interface SourceBundle {
  files: Record<string, string>;
  dependencies: Record<string, string>; // npm package -> semver/range
  config: {
    framework: string;
    language: string;
    entryPoint: string; // e.g. "src/main.tsx"
    buildCommand: string;
  };
}

interface CompilationStats {
  startTime: number;
  endTime: number;
  durationMs: number;
  sourceFiles: number;
}

interface CompilationResult {
  success: boolean;
  html?: string;
  error?: string;
  stats?: CompilationStats;
}

interface LivePreviewFrameProps {
  sourceBundle?: SourceBundle;
  onCompilationResult?: (result: CompilationResult) => void;
}

type Esbuild = {
  initialize: (opts: { wasmURL: string; worker?: boolean }) => Promise<void>;
  build: (opts: any) => Promise<any>;
  version?: string;
};

declare global {
  interface Window {
    esbuild?: Esbuild;
    esbuildInitialized?: boolean;
  }
}

////const ESBUILD_JS = 'https://unpkg.com/esbuild-wasm@0.23.0/esm/browser.min.js';
//const ESBUILD_WASM = 'https://unpkg.com/esbuild-wasm@0.23.0/esbuild.wasm';

const ESBUILD_JS = `${window.location.origin}/cdn/esbuild/browser.min.js`; 
const ESBUILD_WASM = `${window.location.origin}/cdn/esbuild/esbuild.wasm`;

const LivePreviewFrame: React.FC<LivePreviewFrameProps> = ({ sourceBundle, onCompilationResult }) => {
  // Debug esbuild URLs
  console.log('🔧 ESBUILD_JS:', ESBUILD_JS);
  console.log('🔧 ESBUILD_WASM:', ESBUILD_WASM);
  const [isCompiling, setIsCompiling] = useState(false);
  const [compilationResult, setCompilationResult] = useState<CompilationResult | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const esbuildReadyRef = useRef(false);
  const currentBlobUrlRef = useRef<string | null>(null);
  const currentBundleUrlRef = useRef<string | null>(null);

  // Revoke blob URLs to avoid leaks
  const revokeUrl = (url: string | null) => {
    if (!url) return;
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
  };

  const loadHtmlIntoIframe = (html: string) => {
    // Revoke old HTML blob
      if (currentBlobUrlRef.current) {
      revokeUrl(currentBlobUrlRef.current);
        currentBlobUrlRef.current = null;
      }
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      currentBlobUrlRef.current = url;
    if (iframeRef.current) iframeRef.current.src = url;
  };

  // --- esbuild loader / initializer ---------------------------------------
  const ensureEsbuild = async (): Promise<Esbuild> => {
    if (window.esbuild && window.esbuildInitialized) return window.esbuild!;

    // Prefer dynamic import of ESM build
    const mod: any = await import(/* @vite-ignore */ ESBUILD_JS);
    const esbuild: Esbuild = mod?.default ?? mod;
    if (!esbuild?.initialize) throw new Error('Failed to load esbuild-wasm');

    if (!window.esbuildInitialized) {
        await esbuild.initialize({ wasmURL: ESBUILD_WASM, worker: true });
      window.esbuildInitialized = true;
          window.esbuild = esbuild;
          esbuildReadyRef.current = true;
    }
    return esbuild;
  };

  // --- Helpers -------------------------------------------------------------
  const getFileLoader = (path: string): "tsx" | "ts" | "jsx" | "js" | "css" | "json" => {
    if (path.endsWith('.tsx')) return 'tsx';
    if (path.endsWith('.ts')) return 'ts';
    if (path.endsWith('.jsx')) return 'jsx';
    if (path.endsWith('.js')) return 'js';
    if (path.endsWith('.css')) return 'css';
    if (path.endsWith('.json')) return 'json';
    return 'tsx';
  };

  const defaultIndexHtml = () => `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <title>Live Preview</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
      html,body,#root { height:100%; margin:0; }
      #root { display:flex; align-items:center; justify-content:center; font-family: ui-sans-serif, system-ui; color:#222 }
    </style>
  </head>
  <body>
    <div id="root">Loading…</div>
  </body>
</html>`;

  // Build an Import Map for bare specifiers → ESM CDN URLs (esm.sh)
  const makeImportMapJson = (deps: Record<string, string> | undefined): string => {
    const imports: Record<string, string> = {};
    const add = (specifier: string, url: string) => { imports[specifier] = url; };

    const ensure = (pkg: string, version?: string) => {
      // Keep the user's version/range if provided
      const suffix = version ? `@${version}` : '';
      // Use development versions for better debugging
      add(pkg, `https://esm.sh/${pkg}${suffix}?dev`);
    };

    // Core deps (ensure present)
    const reactVer = deps?.react;
    const reactDomVer = deps?.['react-dom'];
    const rrdVer = deps?.['react-router-dom'];

    // Use external esm.sh but with exact versions to ensure compatibility
    const exactReactVer = reactVer || '18.2.0';
    const exactReactDomVer = reactDomVer || '18.2.0';
    const exactRrdVer = rrdVer || '6.8.1';
    
    ensure('react', exactReactVer);
    ensure('react-dom', exactReactDomVer);
    
    // JSX runtime for esbuild "automatic" jsx
    add('react/jsx-runtime', `https://esm.sh/react@${exactReactVer}/jsx-runtime?dev`);
    add('react/jsx-dev-runtime', `https://esm.sh/react@${exactReactVer}/jsx-dev-runtime?dev`);
    add('react-dom/client', `https://esm.sh/react-dom@${exactReactDomVer}/client?dev`);

    // Add all dependencies to import map with pinned React versions
    // This ensures all packages use the same React instance
    console.log('📋 Adding all dependencies to import map with pinned React versions');
    
    if (deps) {
      Object.entries(deps).forEach(([pkg, ver]) => {
        if (imports[pkg]) return; // already mapped above
        
        // Add with deps parameter to ensure they use our React version
        const version = ver || 'latest';
        add(pkg, `https://esm.sh/${pkg}@${version}?dev&deps=react@${exactReactVer},react-dom@${exactReactDomVer}`);
      });
    }
    
    console.log('📋 Total packages in import map:', Object.keys(imports).length);

    return JSON.stringify({ imports }, null, 2);
  };

  const createExecutableHTML = (baseHtml: string, importMapJson: string, bundleUrl: string) => {
    // strip any existing script tags; we'll own the script environment
    const stripped = baseHtml
      .replace(/<script[^>]*>(.|\n|\r)*?<\/script>/g, '')
      .replace(/<script[^>]*src[^>]*><\/script>/g, '')
      .replace(/<script[^>]*type="module"[^>]*src[^>]*><\/script>/g, '');

    // Inject Tailwind CDN if not already present
    let withTailwind = stripped;
    if (!stripped.includes('tailwindcss.com') && !stripped.includes('tailwind')) {
      withTailwind = stripped.replace(
        '</head>',
        '  <script src="https://cdn.tailwindcss.com"></script>\n  </head>'
      );
    }

    // inject import map + es-module-shims + run the module
    const withRunner = withTailwind.replace(
      '</body>',
      `
  <script async src="https://cdn.jsdelivr.net/npm/es-module-shims@1/dist/es-module-shims.min.js"></script>
  <script type="importmap">
${importMapJson}
  </script>
  <script type="module">
    // Provide a small preview context for the app
    window.__LIVE_PREVIEW__ = true;
    window.__LIVE_PREVIEW_BASENAME = '/';
    
    // Import the compiled app module (executes your entry's code)
    // The import map should handle React dependencies automatically
    try {
      console.log('🔧 Importing app module...');
      await import(${JSON.stringify(bundleUrl)});
      console.log('🔧 App module imported successfully');
    } catch (error) {
      console.error('🔧 App module import failed:', error);
      console.error('🔧 Error details:', error.stack);
    }
  </script>
</body>`.trim()
    );

    return withRunner;
  };

  // --- Compile & Preview (ESM path) ---------------------------------------
  const compileAndPreview = async () => {
    if (!sourceBundle) return;

    console.log('🚀🚀🚀 STARTING COMPILATION WITH BUNDLING STRATEGY 🚀🚀🚀');
    console.log('📦 This version BUNDLES all dependencies except React core');
    console.log('📦 React Router, Radix UI, Lucide React should be BUNDLED, not external');

    setIsCompiling(true);
    const stats: CompilationStats = {
      startTime: Date.now(),
      endTime: 0,
      durationMs: 0,
      sourceFiles: Object.keys(sourceBundle.files || {}).length
    };

    try {
      // Prepare esbuild
      const esbuild = await ensureEsbuild();
      const entry = sourceBundle.config?.entryPoint || 'src/main.tsx';

      // Revoke any previous JS bundle URL
      if (currentBundleUrlRef.current) {
        revokeUrl(currentBundleUrlRef.current);
        currentBundleUrlRef.current = null;
      }

      // Calculate exact versions for dependencies
      const reactVer = sourceBundle.dependencies?.react;
      const reactDomVer = sourceBundle.dependencies?.['react-dom'];
      const exactReactVer = reactVer || '18.2.0';
      const exactReactDomVer = reactDomVer || '18.2.0';

      // Build external list - externalize all dependencies
      // The import map ensures they all use the same React version via deps parameter
      const externalPkgs = new Set<string>([
        'react',
        'react-dom',
        'react-dom/client',
        'react/jsx-runtime',
        'react/jsx-dev-runtime',
      ]);
      
      // Add all source dependencies to external list
      Object.keys(sourceBundle.dependencies || {}).forEach((k) => externalPkgs.add(k));
      
      // CRITICAL DEBUG: Show external strategy with pinned React versions
      console.log('🎯 EXTERNAL STRATEGY with PINNED REACT VERSIONS:', {
        'All packages external': true,
        'But all use React': exactReactVer,
        'Total external packages': externalPkgs.size,
        'Sample externals': Array.from(externalPkgs).slice(0, 10)
      });
      
      console.log('✅ All packages will use import map with deps=react@' + exactReactVer);

      // Virtual FS plugin (app files only)
      const virtualFsPlugin = {
          name: 'virtual-fs',
          setup(build: any) {
          const files = sourceBundle.files;

          const resolvePath = (rawPath: string, importer: string) => {
            const stripNs = (p: string) => (p.includes(':') ? p.substring(p.lastIndexOf(':') + 1) : p);
            const importerPath = stripNs(importer);
            const importerDir = importerPath.includes('/') ? importerPath.substring(0, importerPath.lastIndexOf('/')) : '';
                const join = (a: string, b: string) => {
                  const out: string[] = [];
                  const raw = (a ? a + '/' : '') + b;
                  for (const seg of raw.split('/')) {
                    if (!seg || seg === '.') continue;
                if (seg === '..') { out.pop(); continue; }
                    out.push(seg);
                  }
                  return out.join('/');
                };

            if (rawPath.startsWith('@/')) {
              const base = rawPath.replace('@/', 'src/');
              const candidates = [base, `${base}.tsx`, `${base}.ts`, `${base}/index.tsx`, `${base}/index.ts`];
              return candidates.find(p => files[p]);
            }

            if (rawPath.startsWith('./') || rawPath.startsWith('../')) {
              const resolved = join(importerDir, rawPath);
              const candidates = [
                resolved, resolved + '.tsx', resolved + '.ts', resolved + '.jsx', resolved + '.js',
                resolved + '/index.tsx', resolved + '/index.ts', resolved + '/index.jsx', resolved + '/index.js',
                resolved + '.css', resolved + '.json'
              ];
              const foundFile = candidates.find(p => files[p]);
              if (foundFile) {
                console.log(`🔧 Found relative file: ${rawPath} → ${foundFile} (from ${importerDir})`);
              } else {
                console.log(`🔧 Could not find relative file: ${rawPath} in`, candidates);
                const searchTerm = rawPath.replace('./', '').replace('../', '');
                const matchingFiles = Object.keys(files).filter(f => f.includes(searchTerm));
                console.log(`🔧 Available files matching "${searchTerm}":`, matchingFiles);
                console.log(`🔧 All table-related files:`, Object.keys(files).filter(f => f.includes('table') || f.includes('Table')));
              }
              return foundFile;
            }

            return null; // bare specifier -> external
          };

          build.onResolve({ filter: /.*/ }, (args: any) => {
            // Entry goes to virtual namespace
            if (args.kind === 'entry-point') return { path: args.path, namespace: 'virtual' };

            let rawPath = args.path.includes(':') ? args.path.substring(args.path.lastIndexOf(':') + 1) : args.path;

            // Try in the virtual FS (relative or '@/')
            const found = resolvePath(rawPath, args.importer);
            if (found) {
              console.log(`🔧 Resolved ${rawPath} from ${args.importer} → ${found}`);
              return { path: found, namespace: 'virtual' };
            }

            // Check if this bare specifier should be external or bundled
            const shouldBeExternal = externalPkgs.has(rawPath);
            console.log(`🔧 Bare import "${rawPath}": ${shouldBeExternal ? 'EXTERNAL (import map)' : 'BUNDLED (esbuild will bundle)'}`);
            
            // If it's in our external list, mark as external (for import map resolution)
            // Otherwise, return undefined to let esbuild bundle it normally
            if (shouldBeExternal) {
              return { path: args.path, external: true };
            }
            
            // Return undefined to let esbuild try to bundle this package
            return undefined;
            });

            build.onLoad({ filter: /.*/, namespace: 'virtual' }, (args: any) => {
              const content = sourceBundle.files[args.path];
              if (content == null) return null;

            // Inline CSS into <style> at runtime
            if (args.path.endsWith('.css')) {
              const js = `
                const css = ${JSON.stringify(content)};
                const s = document.createElement('style');
                s.setAttribute('data-href', ${JSON.stringify(args.path)});
                s.textContent = css;
                document.head.appendChild(s);
                export default css;
              `;
              return { contents: js, loader: 'js' };
            }

            return { contents: content, loader: getFileLoader(args.path) };
            });
          }
      };

      const result = await esbuild.build({
        entryPoints: [entry],
        bundle: true,
        write: false,
        format: 'esm',          // <<< ESM output
        splitting: false,       // keep single-file output (avoid chunk fetches)
        target: 'es2022',
        platform: 'browser',
        jsx: 'automatic',
        jsxImportSource: 'react',
        sourcemap: false,
        external: Array.from(externalPkgs),
        plugins: [virtualFsPlugin],
      });

      if ((result as any).errors?.length) {
        throw new Error('Compilation errors: ' + (result as any).errors.map((e: any) => e.text).join(', '));
      }

      const jsText: string = (result as any).outputFiles[0].text;

      // Create a blob URL for the ESM bundle
      const jsBlob = new Blob([jsText], { type: 'application/javascript' });
      const bundleUrl = URL.createObjectURL(jsBlob);
      currentBundleUrlRef.current = bundleUrl;

      // Build import map from dependencies (+ core)
      const importMapJson = makeImportMapJson(sourceBundle.dependencies);

      // Use provided index.html or a default
      const baseHTML = sourceBundle.files['index.html'] || defaultIndexHtml();
      const executableHTML = createExecutableHTML(baseHTML, importMapJson, bundleUrl);

      const ok: CompilationResult = {
        success: true,
        html: executableHTML,
        stats: {
          startTime: stats.startTime,
          endTime: (stats.endTime = Date.now()),
          durationMs: (stats.endTime - stats.startTime),
          sourceFiles: stats.sourceFiles
        }
      };

      setCompilationResult(ok);
      onCompilationResult?.(ok);
      loadHtmlIntoIframe(executableHTML);
    } catch (err: any) {
      console.error('Browser compilation failed:', err);
      const fail: CompilationResult = { success: false, error: err?.message || String(err) };
      setCompilationResult(fail);
      onCompilationResult?.(fail);
    } finally {
      setIsCompiling(false);
    }
  };

  // Auto-compile on new/changed source
  useEffect(() => {
    // show a neutral page first
    if (!compilationResult?.html) {
      loadHtmlIntoIframe(defaultIndexHtml());
    }
    if (sourceBundle) {
      compileAndPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sourceBundle)]);

  // --- Open in new tab -----------------------------------------------------
  const handleOpenInNewTab = () => {
    if (!compilationResult?.html) return;
    try {
      const blob = new Blob([compilationResult.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke later
      setTimeout(() => revokeUrl(url), 60_000);
    } catch (e) {
      console.error('Failed to open preview in new tab', e);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      revokeUrl(currentBlobUrlRef.current);
      revokeUrl(currentBundleUrlRef.current);
      currentBlobUrlRef.current = null;
      currentBundleUrlRef.current = null;
    };
  }, []);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {isCompiling && (
        <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Compiling…</Typography>
        </Box>
      )}

      {!sourceBundle && (
        <Alert severity="info">Provide a source bundle to enable compilation.</Alert>
      )}

      {compilationResult && !compilationResult.success && (
        <Alert severity="error">{compilationResult.error}</Alert>
      )}

      <Box sx={{ 
        position: 'relative',
        border: '1px solid #e0e0e0', 
        borderRadius: 1, 
        overflow: 'hidden' 
      }}>
        {compilationResult?.html && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
              backdropFilter: 'blur(10px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
              padding: '2px',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.92))',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(0, 0, 0, 0.15)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <IconButton
              onClick={handleOpenInNewTab}
              size="small"
              title="Open in New Tab"
              sx={{
                color: '#333',
                padding: '4px',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                  color: '#000',
                },
              }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Box>
        )}
        
        <iframe
          ref={iframeRef}
          width="100%"
          height="600"
          style={{ border: 'none' }}
          title="Live Preview"
          sandbox="allow-scripts allow-same-origin"
          src={currentBlobUrlRef.current || 'about:blank'}
        />
      </Box>
    </Box>
  );
};

export default LivePreviewFrame;